/**
 * Unauthenticated branding fetch for the staff portal shell (#1835).
 *
 * Uses plain `fetch` rather than the `api` helper: that helper attaches a
 * bearer token and triggers refresh/logout on 401, which is the wrong
 * behaviour for a call that must work before anyone has signed in.
 */

import type { BrandingConfig, TenantBrandingResponse } from '@/config/branding';

const API_BASE_URL = '/api/v1';

interface PublicBrandingResponse {
  tenantName: string;
  tenantSlug: string;
  branding: BrandingConfig | null;
}

let inFlight: Promise<TenantBrandingResponse> | null = null;

async function loadPublicBranding(): Promise<TenantBrandingResponse> {
  const response = await fetch(`${API_BASE_URL}/public/branding`);
  if (!response.ok) {
    throw new Error('Failed to load branding');
  }
  const data = (await response.json()) as PublicBrandingResponse;
  return {
    tenantName: data.tenantName,
    tenantSlug: data.tenantSlug,
    branding: data.branding ?? null,
    // Public endpoint is presentation-only. Support stays on the authenticated
    // `/tenants/branding` call so the store shape stays backward compatible.
    support: null,
  };
}

export async function fetchPublicBranding(): Promise<TenantBrandingResponse> {
  if (inFlight) return inFlight;
  inFlight = loadPublicBranding().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
